import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { studentTrackingAPI } from '../../api/studentTrackingAPI';
import { studentsAPI } from '../../api/studentsAPI';
import { processAttendanceData } from '../../utils/attendanceCalculations';
import { getDateRange } from '../../utils/timezoneUtils';
import AttendanceChart from './AttendanceChart';
import StudentNotes from './StudentNotes';
import AttendancePatterns from './AttendancePatterns';
import AttendanceSummaryCards from './AttendanceSummaryCards';
import HeaderWithControls from './HeaderWithControls';
import SchoolAnalytics from './SchoolAnalytics';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

export default function StudentTrackingDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading: tenantLoading, currentSchool, getTenantUrl } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const { studentId } = useParams();
  
  // State management
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState(null);
  // Removed unused chartData and patterns state
  const [studentNotes, setStudentNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last7Days');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('overview');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [selectedAnalyticsTab, setSelectedAnalyticsTab] = useState('period'); // 'period' or 'school'

  // Day-specific attendance records state
  const [selectedDayRecords, setSelectedDayRecords] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null); // 'day' or 'week'
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Pagination state for recent attendance records
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  

  // Pagination state for class selector
  const [currentClassPage, setCurrentClassPage] = useState(1);
  const classesPerPage = 8;

  console.log('StudentTrackingDashboard render - students:', students.length, 'selectedStudent:', selectedStudent?.id, 'loading:', loading, 'tenantLoading:', tenantLoading);

  // Simple date formatting helper - backend sends dates in YYYY-MM-DD format
  const formatDateInSchoolTimezone = useCallback((date, options = {}) => {
    if (!date) return 'N/A';
    
    let dateObj;
    
    // Handle Date objects
    if (date instanceof Date) {
      dateObj = date;
    } 
    // Handle ISO strings (e.g., "2025-10-21T12:00:00.000Z")
    else if (typeof date === 'string' && date.includes('T')) {
      dateObj = new Date(date);
    }
    // Handle YYYY-MM-DD strings
    else if (typeof date === 'string' && date.includes('-')) {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    }
    // Fallback
    else {
      return 'N/A';
    }
    
    const defaultOptions = {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
    };
    
    return dateObj.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  }, []);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.relative')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown]);

  // Export Functions
  const exportToXLSX = async () => {
    if (!selectedStudent || !attendanceData) {
      alert('Please select a student and ensure data is loaded before exporting.');
      return;
    }

    setIsExporting(true);
    
    try {
      const filteredData = getFilteredAttendanceData();
      const currentData = filteredData || attendanceData;
      const classFilterText = selectedClassFilter === 'overview' ? 'All Classes' : selectedClassFilter;
      
      // Get date range values for display
      const getDateRangeValues = () => {
        if (dateRange === 'custom') {
          return customDateRange;
        }
        return getDateRange(dateRange);
      };

      const dateRangeValues = getDateRangeValues();
      const dateRangeText = dateRange === 'custom' && dateRangeValues.startDate && dateRangeValues.endDate
        ? `${dateRangeValues.startDate} to ${dateRangeValues.endDate}`
        : dateRange === 'lastWeek' ? 'Last 7 days'
        : dateRange === 'lastMonth' ? 'Last 30 days'
        : dateRange === 'lastQuarter' ? 'Last 90 days'
        : dateRange === 'currentSchoolYear' ? 'Current School Year'
        : currentData.dateRange || 'N/A';

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();
      
      // Student Info Sheet
      const studentInfoData = [
        ['Student Attendance Report'],
        ['Generated on', formatDateInSchoolTimezone(new Date())],
        ['School', currentSchool?.name || 'N/A'],
        [''],
        ['Student Information'],
        ['Name', `${selectedStudent.first_name || ''} ${selectedStudent.last_name || ''}`],
        ['Email', selectedStudent.email || 'N/A'],
        ['Class Filter', classFilterText],
        ['Date Range', dateRangeText]
      ];
      
      const studentInfoWS = XLSX.utils.aoa_to_sheet(studentInfoData);
      XLSX.utils.book_append_sheet(wb, studentInfoWS, 'Student Info');
      
      // Summary Sheet
      const summaryData = [['Attendance Summary'], ['']];
      const summary = currentData.summary;
      const totalRecords = summary?.totalRecords || 0;
      
      summaryData.push(['Status', 'Count', 'Percentage']);
      summaryData.push(['Total Records', totalRecords.toString(), '100%']);
      
      if (summary?.statusCounts) {
        Object.entries(summary.statusCounts).forEach(([status, count]) => {
          const numericCount = parseInt(count) || 0;
          const numericTotal = parseInt(totalRecords) || 1;
          const percentage = numericTotal > 0 ? ((numericCount / numericTotal) * 100).toFixed(1) : '0';
          const statusName = status.charAt(0).toUpperCase() + status.slice(1);
          summaryData.push([statusName, numericCount.toString(), `${percentage}%`]);
        });
      }
      
      if (summary?.attendanceScore !== undefined && summary.attendanceScore !== '0/0') {
        summaryData.push(['Attendance Score', '-', summary.attendanceScore]);
      }
      
      const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');
      
      // Records Sheet
      const recordsData = [['Recent Attendance Records'], ['']];
      const recentRecords = currentData.records ? currentData.records.slice(0, 100) : [];
      
      if (recentRecords.length > 0) {
        recordsData.push(['Date', 'Class', 'Subject', 'Status']);
        recentRecords.forEach(record => {
          recordsData.push([
            record.date ? formatDateInSchoolTimezone(record.date) : 'N/A',
            record.classroom?.name || record.classroom || 'N/A',
            record.period?.name || 'N/A',
            record.status || 'N/A'
          ]);
        });
      } else {
        recordsData.push(['No recent records available']);
      }
      
      const recordsWS = XLSX.utils.aoa_to_sheet(recordsData);
      XLSX.utils.book_append_sheet(wb, recordsWS, 'Records');
      
      // Notes Sheet
      const filteredNotes = getFilteredStudentNotes();
      
      const notesData = [['Student Notes'], ['']];
      
      if (filteredNotes && filteredNotes.length > 0) {
        notesData.push(['Date', 'Class', 'Note']);
        filteredNotes.forEach(note => {
          notesData.push([
            note.date ? formatDateInSchoolTimezone(note.date) : 
            note.created_at ? formatDateInSchoolTimezone(note.created_at) : 'N/A',
            note.classroom?.name || note.classroom || 'Unknown Class',
            note.notes || note.content || note.text || 'No note content'
          ]);
        });
      } else {
        notesData.push(['No notes available']);
      }
      
      const notesWS = XLSX.utils.aoa_to_sheet(notesData);
      XLSX.utils.book_append_sheet(wb, notesWS, 'Notes');
      
      // Download file
      const fileName = `${selectedStudent.first_name}_${selectedStudent.last_name}_Attendance_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Error generating XLSX:', error);
      alert('An error occurred while generating the Excel file. Please try again.');
    } finally {
      setIsExporting(false);
      setShowExportDropdown(false);
    }
  };

  const exportToJPEG = async () => {
    if (!selectedStudent || !attendanceData) {
      alert('Please select a student and ensure data is loaded before exporting.');
      return;
    }

    // Close dropdown immediately without showing loading state
    setShowExportDropdown(false);
    
    try {
      // Scroll to top to ensure we capture from the beginning
      window.scrollTo(0, 0);
      
      // Wait for scroll to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Find the main dashboard container - prioritize data attribute
      let dashboardElement = document.querySelector('[data-dashboard-content]');
      
      if (!dashboardElement) {
        // Try to find the container with max-width class
        dashboardElement = document.querySelector('div[class*="max-w-"]');
      }
      
      if (!dashboardElement) {
        // Fallback to the main content area
        dashboardElement = document.querySelector('main') || document.querySelector('#root > div') || document.getElementById('root');
      }
      
      if (!dashboardElement) {
        throw new Error('Dashboard content not found');
      }
      
      // Get the bounding box to ensure we capture the visible content
      const rect = dashboardElement.getBoundingClientRect();
      
      // Wait for any animations to settle
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(dashboardElement, {
        scale: 1.2,
        useCORS: true,
        backgroundColor: '#ffffff',
        allowTaint: true,
        foreignObjectRendering: false, // Changed to false for better compatibility
        logging: false,
        ignoreElements: (element) => {
          // Ignore dropdown menus and other overlay elements
          return element.classList.contains('absolute') && element.classList.contains('z-50');
        },
        width: rect.width,
        height: rect.height,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });
      
      // Convert to JPEG and download directly
      const link = document.createElement('a');
      link.download = `${selectedStudent.first_name}_${selectedStudent.last_name}_Dashboard_${new Date().toISOString().split('T')[0]}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error generating JPEG:', error);
      alert('An error occurred while generating the image. Please try again.');
    }
    // Note: No finally block needed since we're not using isExporting state
  };

  const exportToPDF = async () => {
    if (!selectedStudent || !attendanceData) {
      alert('Please select a student and ensure data is loaded before exporting.');
      return;
    }

    setIsExporting(true);
    
    try {
      const filteredData = getFilteredAttendanceData();
      const currentData = filteredData || attendanceData;
      const currentDate = formatDateInSchoolTimezone(new Date());
      const classFilterText = selectedClassFilter === 'overview' ? 'All Classes' : selectedClassFilter;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Set up fonts and colors
      pdf.setFontSize(20);
      pdf.setTextColor(30, 64, 175);
      pdf.text('Student Attendance Report', 105, 20, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Generated on ${currentDate}`, 105, 30, { align: 'center' });
      
      // Draw header line
      pdf.setDrawColor(59, 130, 246);
      pdf.setLineWidth(1);
      pdf.line(20, 35, 190, 35);
      
      // Student Information Section
      let yPosition = 50;
      pdf.setFontSize(14);
      pdf.setTextColor(55, 65, 81);
      pdf.text('Student Information', 20, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Name: ${selectedStudent.first_name || ''} ${selectedStudent.last_name || ''}`, 20, yPosition);
      
      yPosition += 7;
      pdf.text(`Email: ${selectedStudent.email || 'N/A'}`, 20, yPosition);
      
      yPosition += 7;
      pdf.text(`Class Filter: ${classFilterText}`, 20, yPosition);
      
      yPosition += 7;
      pdf.text(`Date Range: ${currentData.dateRange || 'N/A'}`, 20, yPosition);
      
      // Simple summary section
      yPosition += 20;
      pdf.setFontSize(14);
      pdf.text('Attendance Summary', 20, yPosition);
      
      yPosition += 10;
      pdf.setFontSize(11);
      const summary = currentData.summary;
      if (summary?.totalRecords) {
        pdf.text(`Total Records: ${summary.totalRecords}`, 20, yPosition);
      }
      
      // Download PDF
      const fileName = `${selectedStudent.first_name}_${selectedStudent.last_name}_Attendance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('An error occurred while generating the PDF. Please try again.');
    } finally {
      setIsExporting(false);
      setShowExportDropdown(false);
    }
  };

  // Load students on component mount
  useEffect(() => {
    const fetchStudents = async () => {
      // Don't load if tenant data is still loading or no school is available
      if (tenantLoading || !currentSchool?.id) return;
      
      try {
        const token = await getAccessTokenSilently();
        // Use getStudentsBySchool to fetch only students from current school
        const result = await studentsAPI.getStudentsBySchool(currentSchool.id, {}, token);
        const studentsArray = Array.isArray(result) ? result : (result.students || []);
        setStudents(studentsArray);
      } catch (err) {
        console.error('Error loading students:', err);
        setError('Failed to load students: ' + err.message);
      }
    };
    
    fetchStudents();
  }, [getAccessTokenSilently, tenantLoading, currentSchool?.id]);

  // Handle URL-based student selection
  useEffect(() => {
    // Check if there's a student ID from navigation state or URL params
    const navigationStudent = location.state?.selectedStudent;
    const navigationStudentId = location.state?.studentId;
    const urlStudentId = studentId;
    
    console.log('🔍 StudentTrackingDashboard: URL-based selection check:', {
      navigationStudent,
      navigationStudentId,
      urlStudentId,
      studentsCount: students.length,
      selectedStudentId: selectedStudent?.id,
      sampleStudent: students[0]
    });
    
    if (students.length > 0) {
      let targetStudent = null;
      
      // Priority 1: Use student ID from URL params (highest priority - represents current navigation intent)
      if (urlStudentId) {
        targetStudent = students.find(s => 
          s.STUDENT_UNIQUE_ID === urlStudentId ||
          s.student_unique_id === urlStudentId ||
          s.id === urlStudentId ||
          String(s.id) === String(urlStudentId)
        );
        console.log('🎯 Priority 1 - URL student ID search result:', targetStudent, {
          urlStudentId,
          studentIds: students.map(s => ({ id: s.id, unique_id: s.STUDENT_UNIQUE_ID || s.student_unique_id }))
        });
      }
      
      // Priority 2: Use complete student object from navigation state (only if no URL param)
      if (!targetStudent && navigationStudent) {
        targetStudent = students.find(s => 
          s.id === navigationStudent.id || 
          s.STUDENT_UNIQUE_ID === navigationStudent.STUDENT_UNIQUE_ID ||
          s.student_unique_id === navigationStudent.student_unique_id
        );
        console.log('🎯 Priority 2 - Navigation student search result:', targetStudent);
      }
      
      // Priority 3: Use student ID from navigation state (lowest priority)
      if (!targetStudent && navigationStudentId) {
        targetStudent = students.find(s => 
          s.STUDENT_UNIQUE_ID === navigationStudentId ||
          s.student_unique_id === navigationStudentId ||
          s.id === navigationStudentId ||
          String(s.id) === String(navigationStudentId)
        );
        console.log('🎯 Priority 3 - Navigation student ID search result:', targetStudent);
      }
      
      // If we found a target student and it's different from current selection
      if (targetStudent && (!selectedStudent || selectedStudent.id !== targetStudent.id)) {
        console.log('🎯 StudentTrackingDashboard: Auto-selecting student from navigation:', targetStudent);
        setSelectedStudent(targetStudent);
        
        // Clear navigation state to prevent re-selection on subsequent renders
        if (location.state) {
          navigate(location.pathname, { replace: true });
        }
      } else if (!targetStudent && urlStudentId) {
        console.log('❌ StudentTrackingDashboard: No student found for ID:', urlStudentId);
      }
    }
  }, [students, studentId, location.state, selectedStudent, navigate, location.pathname]);

  // Load data when student or date range changes
  useEffect(() => {
    const getDateRangeValues = () => {
      if (dateRange === 'custom') {
        return customDateRange;
      }
      
      // Use the centralized date range utility
      return getDateRange(dateRange);
    };

    const fetchAttendanceData = async () => {
      if (!selectedStudent) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const token = await getAccessTokenSilently();
        const dates = getDateRangeValues();
        
        // Get raw data from backend
        const rawData = await studentTrackingAPI.getStudentDailyAttendance(
          selectedStudent.id,
          dates.startDate,
          dates.endDate,
          token
        );
        
        console.log('Received raw attendance data:', rawData);
        
        // Extract unique classes from records
        const classes = [...new Set(rawData.records
          ?.filter(record => record.classroom?.name)
          .map(record => record.classroom.name)
        )].sort();
        
        setAvailableClasses(classes);
        setCurrentClassPage(1); // Reset class pagination when classes change
        
        // Process data on frontend
        const processedData = processAttendanceData(rawData);
        console.log('Processed attendance data:', processedData);
        
        if (processedData) {
          setAttendanceData(processedData);
        }
        
      } catch (err) {
        console.error('Error loading attendance data:', err);
        setError('Failed to load attendance data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (selectedStudent) {
      fetchAttendanceData();
    }
  }, [selectedStudent, dateRange, customDateRange, getAccessTokenSilently]);

  // Load student notes when student is selected
  useEffect(() => {
    const fetchStudentNotes = async () => {
      if (!selectedStudent) return;
      
      setNotesLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const notes = await studentTrackingAPI.getStudentNotes(selectedStudent.id, token);
        setStudentNotes(notes);
      } catch (err) {
        console.error('Error loading student notes:', err);
        // Don't set main error state for notes, just log it
      } finally {
        setNotesLoading(false);
      }
    };
    
    fetchStudentNotes();
  }, [selectedStudent, getAccessTokenSilently]);

  // Load student enrollments when student is selected
  useEffect(() => {
    const fetchStudentEnrollments = async () => {
      if (!selectedStudent) {
        setStudentEnrollments([]);
        return;
      }
      
      try {
        const token = await getAccessTokenSilently();
        const enrollments = await studentsAPI.getStudentEnrollments(selectedStudent.id, token);
        setStudentEnrollments(enrollments);
      } catch (err) {
        console.error('Error loading student enrollments:', err);
        setStudentEnrollments([]);
      }
    };
    
    fetchStudentEnrollments();
  }, [selectedStudent, getAccessTokenSilently]);

  const getDateRangeValues = () => {
    if (dateRange === 'custom') {
      return customDateRange;
    }
    return studentTrackingAPI.getDateRanges()[dateRange];
  };

  const loadAttendanceData = async () => {
    if (!selectedStudent) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessTokenSilently();
      const dates = getDateRangeValues();
      
      // Get raw data from backend
      const rawData = await studentTrackingAPI.getStudentDailyAttendance(
        selectedStudent.id,
        dates.startDate,
        dates.endDate,
        token
      );
      
      // Process data on frontend
      const processedData = processAttendanceData(rawData);
      
      if (processedData) {
        setAttendanceData(processedData);
      }
      
    } catch (err) {
      console.error('Error loading attendance data:', err);
      setError('Failed to load attendance data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  setAttendanceData(null);
    setSelectedClassFilter('overview');
    setAvailableClasses([]);
    setCurrentClassPage(1); // Reset class pagination when clearing student data
    setStudentNotes([]);
    setStudentEnrollments([]); // Clear enrollments when changing student
    clearDaySpecificRecords();
    
    // Update URL to reflect the selected student
    if (student) {
      const studentIdForUrl = student.STUDENT_UNIQUE_ID || student.student_unique_id || student.id;
      const newUrl = getTenantUrl(`/student-tracking/${studentIdForUrl}`, true);
      navigate(newUrl, { replace: true });
    } else {
      // If no student selected, navigate to the base student tracking page
      const newUrl = getTenantUrl('/student-tracking', true);
      navigate(newUrl, { replace: true });
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    if (newRange !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
    setCurrentPage(1); // Reset pagination when date range changes
    setCurrentClassPage(1); // Reset class pagination when date range changes
    clearDaySpecificRecords();
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset pagination when custom date changes
    setCurrentClassPage(1); // Reset class pagination when custom date changes
  };

  // Filter attendance data by selected class
  const getFilteredAttendanceData = () => {
    if (!attendanceData || selectedClassFilter === 'overview') {
      return attendanceData;
    }

    // Filter records by selected class
    const filteredRecords = attendanceData.records?.filter(record => 
      record.classroom?.name === selectedClassFilter
    ) || [];

    // Reprocess the filtered records to get accurate statistics
    const filteredData = processAttendanceData({
      ...attendanceData,
      records: filteredRecords
    });

    return filteredData;
  };

  // Handle chart point click to filter day-specific or week-specific records
  const handleChartPointClick = (clickData) => {
    const dataToUse = getFilteredAttendanceData();
    if (!dataToUse?.records || !clickData) return;
    
    console.log('Chart click debug:', {
      clickData,
      totalRecords: dataToUse.records.length,
      sampleRecord: dataToUse.records[0]
    });
    
    let filteredRecords = [];
    let displayDate = '';
    
    if (clickData.type === 'day') {
      // Filter records for the selected date (already filtered by class if applicable)
      // Both clickData.date and backend records use YYYY-MM-DD format
      const clickedDate = clickData.date; // YYYY-MM-DD format
      
      filteredRecords = dataToUse.records.filter(record => {
        // Backend records are in YYYY-MM-DD format
        const recordDate = record.date;
        const matches = recordDate === clickedDate;
        if (matches) {
          console.log('Record match found:', { record, recordDate, clickedDate });
        }
        return matches;
      });
      
      console.log('Day filter results:', {
        clickedDate,
        totalAvailableRecords: dataToUse.records.length,
        filteredCount: filteredRecords.length,
        sampleFilteredRecord: filteredRecords[0]
      });
      
      displayDate = clickedDate;
      setSelectedPeriod('day');
    } else if (clickData.type === 'week') {
      // Filter records for all dates in the selected week (already filtered by class if applicable)
      // Both clickData.dates and backend records use YYYY-MM-DD format
      filteredRecords = dataToUse.records.filter(record => {
        const recordDate = record.date; // YYYY-MM-DD format from backend
        return clickData.dates.includes(recordDate);
      });
      displayDate = `${clickData.startDate} - ${clickData.endDate}`;
      setSelectedPeriod('week');
    }
    
    setSelectedDate(displayDate);
    setSelectedDayRecords(filteredRecords);
  };

  // Filter student notes by selected class
  const getFilteredStudentNotes = () => {
    if (!studentNotes || selectedClassFilter === 'overview') {
      return studentNotes;
    }

    return studentNotes.filter(note => 
      note.classroom?.name === selectedClassFilter
    );
  };

  // Check if student is enrolled in a specific class (using enrollment data)
  const isStudentEnrolledInClass = (className) => {
    if (!selectedStudent || !studentEnrollments.length) return false;
    
    return studentEnrollments.some(enrollment => 
      enrollment.T_ATT_CLASSROOM?.name === className && 
      enrollment.is_active === true
    );
  };

  // Clear day-specific records when general data changes
  const clearDaySpecificRecords = () => {
    setSelectedDayRecords(null);
    setSelectedDate(null);
    setSelectedPeriod(null);
    setCurrentPage(1); // Reset to first page when clearing day records
  };

  // Get paginated classes for the class selector
  const getPaginatedClasses = () => {
    const startIndex = (currentClassPage - 1) * classesPerPage;
    return availableClasses.slice(startIndex, startIndex + classesPerPage);
  };

  // Get class pagination info
  const getClassPaginationInfo = () => {
    const totalPages = Math.ceil(availableClasses.length / classesPerPage);
    return {
      totalClasses: availableClasses.length,
      totalPages,
      currentPage: currentClassPage,
      hasNextPage: currentClassPage < totalPages,
      hasPrevPage: currentClassPage > 1
    };
  };

  // Removed unused navigateToClassPage helper

  // Get paginated records for the current page
  const getPaginatedRecords = () => {
    if (selectedDayRecords) {
      // For day-specific records, don't paginate (they're already filtered)
      return selectedDayRecords;
    }
    
    const allRecords = getFilteredAttendanceData()?.records || [];
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return allRecords.slice(startIndex, endIndex);
  };

  // Get pagination info
  const getPaginationInfo = () => {
    if (selectedDayRecords) {
      return null; // No pagination for day-specific records
    }
    
    const allRecords = getFilteredAttendanceData()?.records || [];
    const totalRecords = allRecords.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    
    return {
      totalRecords,
      totalPages,
      currentPage,
      recordsPerPage,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    };
  };

  // Show loading if tenant context is still loading
  if (tenantLoading) {
    return (
      <div className="max-w-[90rem] mx-auto px-0 py-2">  
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  if (loading && !attendanceData) {
    return (
      <div className="max-w-[90rem] mx-auto px-0 py-2">  
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Loading student tracking data...</div>
        </div>
      </div>
    );
  }

  console.log('Rendering main component');
  return (
    <div className=" mx-auto px-0 py-2" data-dashboard-content>  
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Student Attendance Tracking</h1>
        <p className="text-slate-600">Track individual student attendance patterns and analytics</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-600">{error}</div>
          <button 
            onClick={() => selectedStudent && loadAttendanceData()}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Try again
          </button>
        </div>
      )}

      {/* Debug logging */}

      {/* Consolidated Header with Controls */}
      <HeaderWithControls
        student={selectedStudent}
        dateRange={attendanceData?.dateRange}
        attendanceRate={attendanceData?.summary?.attendanceScore || '0/0'}
        students={students}
        onStudentSelect={handleStudentSelect}
        dateRangeOptions={[
          { value: 'last7Days', label: 'Last 7 Days' },
          { value: 'last30Days', label: 'Last 30 Days' },
          { value: 'last120Days', label: 'Last 120 Days' },
          { value: 'custom', label: 'Custom Range' }
        ]}
        selectedDateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        customDateRange={customDateRange}
        onCustomDateChange={handleCustomDateChange}
      />

      {/* Content */}
      {!selectedStudent ? (
        <div className="space-y-8 ">

          {/* Empty State */}
          <div className="bg-white text-center py-16 h-[500px] rounded-lg shadow-sm border border-slate-200 flex items-center justify-center">
            <div>
              <div className="bg-gray-100 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">No student selected</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                Select a student from the sidebar to view their detailed attendance tracking data and analytics.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Analytics Tabs - Above everything else */}
          {attendanceData && (
            <div className="mb-6">
              <div className="flex items-center justify-center">
                <div className="flex space-x-1 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-1.5 border border-blue-100">
                  <button
                    onClick={() => setSelectedAnalyticsTab('period')}
                    className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                      selectedAnalyticsTab === 'period'
                        ? 'bg-white text-blue-600 shadow-lg shadow-blue-100 transform scale-105'
                        : 'text-slate-600 hover:text-blue-600 hover:bg-white/50'
                    }`}
                  >
                    <span className="mr-2">📊</span>
                    Period Analytics
                  </button>
                  <button
                    onClick={() => setSelectedAnalyticsTab('school')}
                    className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
                      selectedAnalyticsTab === 'school'
                        ? 'bg-white text-purple-600 shadow-lg shadow-purple-100 transform scale-105'
                        : 'text-slate-600 hover:text-purple-600 hover:bg-white/50'
                    }`}
                  >
                    <span className="mr-2">🏫</span>
                    Daily Analytics
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Period Analytics Content */}
          {selectedAnalyticsTab === 'period' && (
            <>
              {/* Header */}
              <div className="text-center mb-0">
                <p className="text-slate-600">
                  Attendance overview for <span className="font-semibold text-purple-600">{selectedStudent?.first_name} {selectedStudent?.last_name}</span>
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Based on class presence throughout the day
                </p>
              </div>

          {/* Class Filter Tabs - Above Summary Cards */}
          {attendanceData && availableClasses.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between w-full">
                {/* Export Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                    disabled={!selectedStudent || !attendanceData || isExporting}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                      !selectedStudent || !attendanceData || isExporting
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100'
                    }`}
                  >
                    {isExporting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Export</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {showExportDropdown && !isExporting && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
                      <div className="py-1">
                        <button
                          onClick={exportToXLSX}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Export as Excel (.xlsx)</span>
                        </button>
                        <button
                          onClick={exportToJPEG}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Export as Image (.jpg)</span>
                        </button>
                        <button
                          onClick={exportToPDF}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span>Export as PDF</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Class Filter Tabs */}
                <div className="flex items-center space-x-2">
                  {/* Navigation Buttons */}
                  {availableClasses.length > classesPerPage && (
                    <button
                      onClick={() => setCurrentClassPage(prev => Math.max(1, prev - 1))}
                      disabled={!getClassPaginationInfo().hasPrevPage}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        getClassPaginationInfo().hasPrevPage
                          ? 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                          : 'text-slate-300 cursor-not-allowed'
                      }`}
                      title="Previous classes"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Class Tabs Container */}
                  <div className="flex items-center space-x-1 bg-slate-100 rounded-xl p-1">
                    {/* Overview Tab */}
                    <button
                      onClick={() => {
                        setSelectedClassFilter('overview');
                        clearDaySpecificRecords();
                      }}
                      className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                        selectedClassFilter === 'overview'
                          ? 'bg-white text-blue-600 shadow-lg shadow-blue-100'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
                      }`}
                    >
                      📊 Overview
                    </button>
                    
                    {/* Class Tabs */}
                    {getPaginatedClasses().map((className) => (
                      <button
                        key={className}
                        onClick={() => {
                          setSelectedClassFilter(className);
                          clearDaySpecificRecords();
                        }}
                        className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 whitespace-nowrap ${
                          selectedClassFilter === className
                            ? 'bg-white text-blue-600 shadow-lg shadow-blue-100'
                            : 'text-slate-600 hover:text-slate-800 hover:bg-white/50'
                        }`}
                      >
                        📚 {className}
                        {isStudentEnrolledInClass(className) && (
                          <sup className="text-xs font-bold text-yellow-600 ml-1 bg-yellow-100 px-1 py-0.5 rounded-full border border-yellow-300">E</sup>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Navigation Buttons */}
                  {availableClasses.length > classesPerPage && (
                    <>
                      <button
                        onClick={() => setCurrentClassPage(prev => Math.min(getClassPaginationInfo().totalPages, prev + 1))}
                        disabled={!getClassPaginationInfo().hasNextPage}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          getClassPaginationInfo().hasNextPage
                            ? 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                            : 'text-slate-300 cursor-not-allowed'
                        }`}
                        title="Next classes"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      
                      {/* Page Indicator */}
                      <div className="text-xs text-slate-500 px-2">
                        {getClassPaginationInfo().currentPage} / {getClassPaginationInfo().totalPages}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          {attendanceData && (
            <AttendanceSummaryCards 
              summary={getFilteredAttendanceData()?.summary || attendanceData.summary}
              student={attendanceData.student}
              dateRange={attendanceData.dateRange}
            />
          )}

          {/* Attendance Insights - Horizontal layout below cards */}
          {attendanceData && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  Attendance Insights
                  {selectedClassFilter !== 'overview' && (
                    <span className="ml-2 text-sm font-normal text-blue-600">
                      - {selectedClassFilter}
                    </span>
                  )}
                </h3>
              </div>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-slate-500">Loading insights...</div>
                </div>
              ) : getFilteredAttendanceData()?.patterns ? (
                <AttendancePatterns patterns={getFilteredAttendanceData().patterns} />
              ) : (
                <div className="flex items-center justify-center h-32 text-slate-500">
                  No insights available
                </div>
              )}
            </div>
          )}

          {/* Charts and Analysis */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-6">
            {/* Attendance Chart - Takes up 3 columns on xl screens */}
            <div className="xl:col-span-3">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-slate-500">Loading chart...</div>
                </div>
              ) : getFilteredAttendanceData()?.chartData ? (
                <AttendanceChart 
                  chartData={getFilteredAttendanceData().chartData}
                  student={getFilteredAttendanceData().student}
                  dateRange={getFilteredAttendanceData().dateRange}
                  selectedClass={selectedClassFilter}
                  onPointClick={handleChartPointClick}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-500">
                  No chart data available
                </div>
              )}
            </div>

            {/* Student Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                Recent Notes
                {selectedClassFilter !== 'overview' && (
                  <span className="ml-2 text-sm font-normal text-blue-600">
                    - {selectedClassFilter}
                  </span>
                )}
              </h3>
              <StudentNotes 
                notes={getFilteredStudentNotes()}
                loading={notesLoading}
              />
            </div>
          </div>

          {/* Recent Records */}
          {(getFilteredAttendanceData()?.records?.length > 0 || selectedDayRecords?.length > 0) && (
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  {selectedDayRecords ? (
                    <span>
                      Attendance Records for {selectedPeriod === 'week' ? 'Week of ' : ''}{selectedDate}
                      {selectedClassFilter !== 'overview' && (
                        <span className="ml-2 text-sm font-normal text-blue-600">
                          - {selectedClassFilter}
                        </span>
                      )}
                      {selectedDayRecords.length > 0 && (
                        <span className="ml-2 text-sm font-normal text-slate-500">
                          ({selectedDayRecords.length} record{selectedDayRecords.length !== 1 ? 's' : ''})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>
                      Recent Attendance Records
                      {selectedClassFilter !== 'overview' && (
                        <span className="ml-2 text-sm font-normal text-blue-600">
                          - {selectedClassFilter}
                        </span>
                      )}
                    </span>
                  )}
                </h3>
                {selectedDayRecords && (
                  <button
                    onClick={clearDaySpecificRecords}
                    className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1 rounded-md border border-blue-200 hover:bg-blue-50 transition-colors"
                  >
                    View All Records
                  </button>
                )}
              </div>
              
              {selectedDayRecords && selectedPeriod === 'week' && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    📊 Showing all attendance records for the selected week. Click individual days in the Timeline view for day-specific records.
                  </p>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Class/Period</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {getPaginatedRecords().map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {formatDateInSchoolTimezone(record.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              record.status.toLowerCase() === 'present' ? 'bg-green-100 text-green-800' :
                              record.status.toLowerCase() === 'absent' ? 'bg-red-100 text-red-800' :
                              record.status.toLowerCase() === 'late' ? 'bg-yellow-100 text-yellow-800' :
                              record.status.toLowerCase() === 'excused' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            <div>
                              <div className="font-medium">{record.classroom?.name || record.classroom}</div>
                              {record.period && (
                                <div className="text-slate-500 text-xs">
                                  {record.period.name} {record.period.startTime && record.period.endTime && `(${record.period.startTime} - ${record.period.endTime})`}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {record.lateMinutes ? `${record.lateMinutes} min late` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {record.notes ? (
                              <span className="truncate block max-w-xs" title={record.notes}>
                                {record.notes}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                {getPaginationInfo() && getPaginationInfo().totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 rounded-b-lg">
                    <div className="flex items-center text-sm text-slate-700">
                      <span>
                        Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, getPaginationInfo().totalRecords)} of {getPaginationInfo().totalRecords} records
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={!getPaginationInfo().hasPrevPage}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          getPaginationInfo().hasPrevPage
                            ? 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                            : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                        }`}
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: getPaginationInfo().totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return (
                              page === 1 ||
                              page === getPaginationInfo().totalPages ||
                              Math.abs(page - currentPage) <= 1
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <span className="px-2 py-1 text-slate-400">...</span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                    page === currentPage
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={!getPaginationInfo().hasNextPage}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          getPaginationInfo().hasNextPage
                            ? 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                            : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
            </div>
          )}
            </>
          )}

          {/* School Analytics Content */}
          {selectedAnalyticsTab === 'school' && (
            <SchoolAnalytics 
              attendanceData={getFilteredAttendanceData() || attendanceData}
              student={selectedStudent}
              dateRange={dateRange}
            />
          )}
        </>
      )}
    </div>
  );
}